<template>
    <flex-container>
        <flex-container gap="sm">
            <flex-container>
                <text-decorator bold :size="FontSize.xs">
                    Medewerkers
                </text-decorator>
            </flex-container>
            <back-button :to="{ name: 'user-list' }" />
            <text-heading>
                Medewerker bewerken
            </text-heading>
        </flex-container>
        <flex-container
            :screen-sizes="{
                desktop: 5,
            }"
        >
            <card-normal>
                <flex-container gap="lg">
                    <text-description>
                        Je bevindt je in het configuratiescherm Medewerkers bewerken.
                        Hier heb jij de mogelijkheid een medewerker te wijzigen,
                        deze de juiste rechten te geven en een neiuwe wachtwoord te genereren.
                    </text-description>

                    <UserForm ref="userForm" :user-id="userId" />

                    <flex-container justify-content="flex-end" align-items="center">
                        <button-text @click="$router.push({ name: 'user-list' })">
                            Annuleren
                        </button-text>
                        <button-block :disabled="!isValid" @click="save">
                            Aanpassen
                        </button-block>
                    </flex-container>
                </flex-container>
            </card-normal>
        </flex-container>
    </flex-container>
</template>

<script setup lang="ts">
    import BackButton from '@/shared/components/BackButton.vue';
    import { useRouter } from 'vue-router';
    import { computed, onMounted, ref } from 'vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { ChangeUserKeyflowRoleCommand, KeyflowRole, User, UserId } from '@keyvue/one-api-client';
    import { FontSize, NotificationBlock, useInteractionBlocker, useNotify } from '@keyvue/one-ui';
    import UserForm, { type ExposedUserForm } from '@/company/user/components/UserForm.vue';
    import { UserAPI, Gender as LegacyGender, Branch } from '@keyvue/one-api-client-legacy';

    const router = useRouter();
    const { client } = useClient();
    const { notify } = useNotify();
    const { blockInteractionForLoad } = useInteractionBlocker();

    const userForm = ref<ExposedUserForm>();
    const validation = computed(() => userForm.value?.formValidation.formValidationBindings);
    const isValid = computed<boolean>(() => validation.value?.isValid.value ?? false);
    const userId = computed<string>(() => router.currentRoute.value.params.userId.toString());

    onMounted(async () => {
        const user = await client.get(User, userId.value);
        const legacyUser = await UserAPI.get(userId.value);

        userForm.value?.setForm({
            firstName: user.name.firstName ?? '',
            middleName: user.name.middleName ?? '',
            lastName: user.name.lastName ?? '',
            gender: user.gender !== null ? {
                value: user.gender.value,
                label: user.gender.label,
            } : null,
            birthDate: legacyUser.birthdate !== null ? new Date(legacyUser.birthdate) : null,
            phoneNumber: user.phoneNumber ?? '',
            mobilePhoneNumber: user.mobilePhoneNumber ?? '',
            email: user.email?.value ?? '',
            teams: user.winckTeams ?? [],
            realworksAccountManagerId: legacyUser.realworksAccountManager?.toString() ?? '',
            hasAccess: user.keyflowRole !== null,
            isManager: KeyflowRole.COMPANY_MANAGER.equals(user.keyflowRole ?? ''),
        });
    });

    const keyflowRole = computed(() => {
        const formValues = userForm.value?.form;

        if (formValues?.isManager) {
            return KeyflowRole.COMPANY_MANAGER;
        }

        if (formValues?.hasAccess) {
            return KeyflowRole.EMPLOYEE;
        }

        return null;
    });

    async function save() {
        const clearBlock = blockInteractionForLoad('Bezig met het opslaan van de medewerker');

        const formValues = userForm.value?.form;

        if (!formValues) {
            clearBlock();

            notify({
                component: NotificationBlock,
                props: {
                    title: 'We konden de medewerker niet opslaan',
                    text: 'Probeer het later nog een keer.',
                    color: 'red-500',
                },
                duration: 4000,
            });

            return;
        }

        const user = await client.get(User, userId.value);

        const legacyUser = await UserAPI.get(userId.value);
        legacyUser.firstname = formValues.firstName;
        legacyUser.middlename = formValues.middleName;
        legacyUser.lastname = formValues.lastName;
        legacyUser.gender = (formValues.gender?.value ?? 'unknown') as LegacyGender;
        legacyUser.birthdate = formValues.birthDate?.toDateString() ?? '';
        legacyUser.phoneNumber = formValues.phoneNumber ?? '';
        legacyUser.mobilePhoneNumber = formValues.mobilePhoneNumber ?? '';
        legacyUser.email = formValues.email;
        legacyUser.branches = [...user.keyVueTeams, ...formValues.teams].map((team) => ({ id: team.id.value }) as Branch);
        legacyUser.realworksAccountManager = parseInt(formValues.realworksAccountManagerId, 10);

        await UserAPI.update(
            userId.value,
            legacyUser,
        );

        await client.dispatch(new ChangeUserKeyflowRoleCommand(new UserId(userId.value), keyflowRole.value));

        clearBlock();

        notify({
            component: NotificationBlock,
            props: {
                title: 'Gelukt!',
                text: 'We hebben de medewerker opgeslagen.',
                color: 'green-500',
            },
            duration: 4000,
        });

        await router.push({ name: 'user-list' });
    }
</script>
